import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import { AlertTriangle, Clock, ChevronRight } from "lucide-react";

const MissingData = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMissing = () => {
    setLoading(true);
    api.get("/production/missing-data")
      .then((r) => setJobs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMissing(); }, []);

  const getRowColor = (hours) => {
    if (hours >= 8) return "border-red-200 bg-red-50";
    if (hours >= 4) return "border-amber-200 bg-amber-50";
    return "border-slate-200 bg-white";
  };

  const getBadgeColor = (hours) => {
    if (hours >= 8) return "bg-red-100 text-red-700";
    return "bg-amber-100 text-amber-700";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Missing Data" showBack backPath="/home" />

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Alert banner */}
        <div className="bg-[#DC2626] rounded-2xl p-4 text-white mb-4 flex items-start gap-3">
          <AlertTriangle size={24} className="flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-extrabold text-lg">Missing Production Data</h2>
            <p className="text-white/70 text-sm">लापता उत्पादन डेटा — Open jobs older than 4 hours</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            {jobs.length} job{jobs.length !== 1 ? "s" : ""} overdue
          </p>
          <button onClick={fetchMissing} className="text-xs text-[#1A3560] font-bold flex items-center gap-1">
            <Clock size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock size={32} className="text-[#15803D]" />
            </div>
            <p className="font-bold text-slate-900">No missing data!</p>
            <p className="text-slate-400 text-sm mt-1">All production jobs are up to date</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <button
                key={job.job_id}
                data-testid={`missing-job-${job.job_id}`}
                onClick={() => navigate(`/production/update/${job.job_id}`)}
                className={`w-full rounded-2xl border-2 p-4 text-left hover:shadow-md active:scale-[0.98] transition-all ${getRowColor(job.hours_elapsed)}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-extrabold text-slate-900 text-base">{job.job_id}</span>
                      {job.hours_elapsed >= 8 && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Past Shift!</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">T{job.tool_no} — {job.part_name}</p>
                    <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-slate-500">
                      <span>Machine: <b className="text-slate-700">{job.machine_code}</b></span>
                      <span>Operator: <b className="text-slate-700">{job.operator_name}</b></span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-3">
                    <span className={`text-sm font-extrabold px-3 py-1.5 rounded-xl ${getBadgeColor(job.hours_elapsed)}`}>
                      {job.hours_elapsed?.toFixed(1)}h
                    </span>
                    <ChevronRight size={18} className="text-slate-400" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MissingData;
